import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { MainLayout } from "./layouts/MainLayout";
import { ProductList } from "./pages/ProductList";
import { ProductDetail } from "./pages/ProductDetail";
import { ProductDetailInfo } from "./pages/ProductDetailInfo";
import { Orders } from "./pages/Orders";
import { OrderForm } from "./pages/OrderForm";
import { CustomersList as Customers } from "./pages/CustomersV2/List";
import { CustomerDetail } from "./pages/customers/CustomerDetail";
import { CustomerCreate } from "./pages/CustomerCreate";
import { BeneficiaryEdit } from "./pages/customers/BeneficiaryEdit";
import { InsuredEdit } from "./pages/customers/InsuredEdit";
import { Profile } from "./pages/Profile";
import { OrderCorrectionList } from "./pages/OrderCorrectionList";
import { OrderAuditLog } from "./pages/OrderAuditLog";
import { RiskAssessmentForm } from "./pages/OrderForms/RiskAssessmentForm";
import { OrderSuccess } from "./pages/OrderForms/OrderSuccess";
import { OrderDetail } from "./pages/OrderDetail";
import { OrderSubmitSuccess } from "./pages/OrderSubmitSuccess";
import CustomerConfirm from "./pages/h5/CustomerConfirm";
import CustomerConfirmSuccess from "./pages/h5/CustomerConfirmSuccess";
import FilePreview from "./pages/FilePreview";
import ErrorBoundary from "./components/ErrorBoundary";
import { useMetadataStore, ensureAuthenticated, useConfigStore } from "@ifa/utils";
import { isIOSStandaloneMode } from "@/utils/platform";
import KeyboardTestPage from "./pages/KeyboardTestPage";
import "./App.css";
import { useViewportHeight } from "@/hooks/useViewportHeight";
import { DebugPanel } from "./components/DebugPanel";
import { Spin } from "antd";

function App() {
  const brandConfig = useConfigStore((state) => state.brandConfig);

  // 根据品牌配置动态更新页面标题和 favicon
  useEffect(() => {
    // 更新页面标题
    document.title = brandConfig.name;

    // 更新 favicon
    const link = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (link) {
      link.href = brandConfig.favicon;
    }
  }, [brandConfig.name, brandConfig.favicon]);
  // 在最早期执行 Standalone Mode 检测
  useEffect(() => {
    if (isIOSStandaloneMode()) {
      document.documentElement.classList.add("ios-standalone");
      document.body.classList.add("ios-standalone");
      console.log("=== iOS Standalone Mode 检测 ===");
      console.log("✅ Standalone Mode 已激活");
      console.log("window.innerHeight:", window.innerHeight);
      console.log("visualViewport.height:", window.visualViewport?.height);
      console.log(
        "差异:",
        window.innerHeight - (window.visualViewport?.height || 0),
        "px"
      );
    } else {
      console.log("=== 普通浏览器模式 ===");
    }
  }, []);

  useViewportHeight();

  const navigate = useNavigate();
  const location = useLocation();
  const fetchMetadata = useMetadataStore((state) => state.fetchMetadata);
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);

  // SSO 认证检查
  useEffect(() => {
    const checkAuth = async () => {
      // h5 路由无需认证（客户确认页面）
      if (location.pathname.startsWith("/h5")) {
        setIsChecking(false);
        setIsAuthed(true);
        return;
      }

      // 使用 SSO 客户端的完整认证流程
      const authenticated = await ensureAuthenticated();

      if (authenticated) {
        setIsAuthed(true);
        // 认证成功后加载全局元数据
        try {
          await fetchMetadata();
        } catch (error) {
          console.error("加载元数据失败:", error);
        }
      }
      setIsChecking(false);
    };

    checkAuth();
  }, [location.pathname, fetchMetadata]);

  // 正在检查认证状态
  if (isChecking) {
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // 非 h5 路由且未认证，显示加载（实际上会自动跳转到统一登录页）
  if (!isAuthed && !location.pathname.startsWith("/h5")) {
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <DebugPanel />
      <Routes>
        <Route
          path="/h5/order/confirm/:orderId/:flowRecordId"
          element={<CustomerConfirm />}
        />
        <Route path="/h5/order/success" element={<CustomerConfirmSuccess />} />
        <Route path="/" element={<MainLayout />}>
          <Route index element={<Navigate to="/orders" replace />} />
          <Route path="products" element={<ProductList />} />
          <Route path="products/:productId" element={<ProductDetail />} />
          <Route
            path="products/:productId/info"
            element={<ProductDetailInfo />}
          />
          <Route path="orders" element={<Orders />} />
          <Route path="customers" element={<Customers />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        {/* Full screen pages without sidebar */}
        <Route path="product-select" element={<ProductList />} />
        <Route path="orders/new" element={<OrderForm />} />
        <Route path="orders/confirmation" element={<OrderForm />} />
        <Route path="preview" element={<FilePreview />} />
        <Route
          path="orders/:orderId"
          element={
            <ErrorBoundary>
              <OrderDetail />
            </ErrorBoundary>
          }
        />
        <Route path="orders/:orderId/success" element={<OrderSuccess />} />
        <Route
          path="orders/:orderId/submit-success"
          element={<OrderSubmitSuccess />}
        />
        <Route path="orders/:orderId/risk" element={<RiskAssessmentForm />} />
        <Route path="orders/:orderId/edit" element={<OrderForm />} />
        <Route
          path="orders/:orderId/correction"
          element={<OrderCorrectionList />}
        />
        <Route path="orders/:orderId/audit-log" element={<OrderAuditLog />} />
        {/* Reusing OrderForm for correction editing for now */}
        <Route
          path="orders/:orderId/correction/:recordId/edit"
          element={<OrderForm />}
        />

        <Route path="customers/new" element={<CustomerCreate />} />
        <Route path="customers/:customerId" element={<CustomerDetail />} />
        <Route
          path="customers/:customerId/beneficiaries/:beneficiaryId/edit"
          element={<BeneficiaryEdit />}
        />
        <Route
          path="customers/:customerId/insureds/:insuredId/edit"
          element={<InsuredEdit />}
        />

        {/* 键盘适配测试页面（开发调试用） */}
        <Route path="test/keyboard" element={<KeyboardTestPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
